import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Asset, CbctProcessingApi, CbctProcessingStatusResult } from './types';

export type CbctProcessingPhase =
    'checking' | 'idle' | 'queued' | 'running' | 'succeeded' | 'failed';

export interface CbctProcessingJob {
    phase: CbctProcessingPhase;
    /** Failure reason, set when phase is 'failed'. */
    error?: string;
}

const POLL_INTERVAL_MS = 5000;
const SLOW_POLL_INTERVAL_MS = 15000;
/** Ticks polled at the fast interval before backing off (~2 min at 5s). */
const FAST_POLL_TICKS = 24;
/** Ticks a fresh trigger may report NOT_STARTED before we believe it (job-row visibility lag). */
const START_GRACE_TICKS = 3;

const toJob = (result: CbctProcessingStatusResult): CbctProcessingJob => {
    switch (result.status) {
        case 'QUEUED':
            return { phase: 'queued' };
        case 'RUNNING':
            return { phase: 'running' };
        case 'SUCCEEDED':
            return { phase: 'succeeded' };
        case 'FAILED':
            return { phase: 'failed', error: result.error };
        default:
            return { phase: 'idle' };
    }
};

export interface UseCbctProcessingJobsOptions {
    /** The optional FilesApi capability. Absent = the hook is inert. */
    api?: CbctProcessingApi;
    /** Called once a job succeeds, so the host refetches assets and the file
     *  arrives with its manifest/binary refs. */
    refreshAssets: () => Promise<void>;
}

/** Session-scoped job tracker: one poll loop per job, survives gate unmount. */
export function useCbctProcessingJobs({ api, refreshAssets }: UseCbctProcessingJobsOptions) {
    const [jobs, setJobs] = useState<Record<string, CbctProcessingJob>>({});
    // One poll session per key; the record's identity lets in-flight status
    // promises detect they've been superseded (see startPolling's isCurrent).
    const timersRef = useRef<Map<string, { timer: number }>>(new Map());
    // Keys whose SUCCEEDED already triggered a refresh - guards the hot loop
    // refresh → new asset identity → re-watch → SUCCEEDED → refresh again.
    const refreshedRef = useRef<Set<string>>(new Set());
    // Ref-held so poll callbacks always see the host's latest refresh without
    // re-arming intervals when the prop identity changes.
    const refreshRef = useRef(refreshAssets);
    useEffect(() => {
        refreshRef.current = refreshAssets;
    }, [refreshAssets]);

    const setJob = useCallback((key: string, job: CbctProcessingJob) => {
        setJobs((prev) => ({ ...prev, [key]: job }));
    }, []);

    const stopPolling = useCallback((key: string) => {
        const session = timersRef.current.get(key);
        if (session) {
            window.clearTimeout(session.timer);
            timersRef.current.delete(key);
        }
    }, []);

    const applyResult = useCallback(
        (key: string, result: CbctProcessingStatusResult) => {
            setJob(key, toJob(result));
            if (result.status === 'SUCCEEDED') {
                stopPolling(key);
                // One refresh per success: metadata can lag the job row (written
                // out-of-band by odo-processing), so a repeat would hot-loop.
                if (!refreshedRef.current.has(key)) {
                    refreshedRef.current.add(key);
                    void refreshRef.current();
                }
            } else if (result.status === 'FAILED' || result.status === 'NOT_STARTED') {
                stopPolling(key);
            }
        },
        [setJob, stopPolling]
    );

    const startPolling = useCallback(
        (asset: Asset, graceTicks = 0) => {
            if (!api) return;
            const key = String(asset.assetId);
            if (timersRef.current.has(key)) return;
            const session = { timer: 0 };
            timersRef.current.set(key, session);
            let ticks = 0;
            let grace = graceTicks;
            const isCurrent = () => timersRef.current.get(key) === session;
            const scheduleNext = () => {
                const interval = ticks < FAST_POLL_TICKS ? POLL_INTERVAL_MS : SLOW_POLL_INTERVAL_MS;
                session.timer = window.setTimeout(() => {
                    ticks += 1;
                    api.getStatus(asset)
                        .then((result) => {
                            if (!isCurrent()) return;
                            // Fresh triggers can report NOT_STARTED until the job
                            // row lands - keep the queued state for a few ticks.
                            if (result.status === 'NOT_STARTED' && grace > 0) {
                                grace -= 1;
                                scheduleNext();
                                return;
                            }
                            applyResult(key, result);
                            if (isCurrent()) scheduleNext();
                        })
                        .catch(() => {
                            // Transient poll failure: keep the last state, retry.
                            if (isCurrent()) scheduleNext();
                        });
                }, interval);
            };
            scheduleNext();
        },
        [api, applyResult]
    );

    /** Check the server-side status of an asset's job (e.g. when the gate opens)
     *  and start polling if one is already in flight. Idempotent while polling. */
    const watch = useCallback(
        async (asset: Asset) => {
            if (!api) return;
            const key = String(asset.assetId);
            if (timersRef.current.has(key)) return;
            // Success already refreshed once but metadata may still lag: hold a
            // succeeded state instead of re-polling (a "Process scan" button
            // here could double-trigger the job).
            if (refreshedRef.current.has(key)) {
                setJob(key, { phase: 'succeeded' });
                return;
            }
            setJob(key, { phase: 'checking' });
            try {
                const result = await api.getStatus(asset);
                applyResult(key, result);
                if (result.status === 'QUEUED' || result.status === 'RUNNING') {
                    startPolling(asset);
                }
            } catch {
                setJob(key, { phase: 'idle' });
            }
        },
        [api, setJob, applyResult, startPolling]
    );

    const start = useCallback(
        async (asset: Asset, password?: string) => {
            if (!api) return;
            const key = String(asset.assetId);
            stopPolling(key);
            // A fresh job gets its own one-shot refresh on success.
            refreshedRef.current.delete(key);
            setJob(key, { phase: 'queued' });
            try {
                await api.trigger(asset, password ? { password } : undefined);
                startPolling(asset, START_GRACE_TICKS);
            } catch (error) {
                setJob(key, {
                    phase: 'failed',
                    error: error instanceof Error ? error.message : 'Failed to start processing.'
                });
            }
        },
        [api, setJob, stopPolling, startPolling]
    );

    // Reset on api swap (patient change) and unmount: stale intervals refresh the wrong patient.
    useEffect(() => {
        const timers = timersRef.current;
        const refreshed = refreshedRef.current;
        return () => {
            timers.forEach((session) => window.clearTimeout(session.timer));
            timers.clear();
            refreshed.clear();
            setJobs((prev) => (Object.keys(prev).length === 0 ? prev : {}));
        };
    }, [api]);

    /** AssetIds (stringified) with a queued/running job - drives card badges. */
    const activeAssetIds = useMemo(() => {
        const ids = Object.entries(jobs)
            .filter(([, job]) => job.phase === 'queued' || job.phase === 'running')
            .map(([key]) => key);
        return new Set(ids);
    }, [jobs]);

    return {
        enabled: !!api,
        jobs,
        activeAssetIds,
        watch,
        start
    };
}

export type CbctProcessingJobs = ReturnType<typeof useCbctProcessingJobs>;
